import type { CameraDistanceId } from "./camera-distance";
import type { RideProgressPlan } from "./ride";
import { smoothedRouteHeading } from "./route-heading";

// Spatial filtering rejects sample noise; centered film-time filtering eases
// corners without accumulating a trailing heading error through gradual bends.
const LOCAL_RADIUS_KILOMETERS = 0.03;
const SMOOTHING_RADIUS_KILOMETERS = 0.06;
const SMOOTHING_SECONDS = 0.25;
const MAX_SAMPLES = 16_384;

type HeadingAt = (journeyProgress: number) => number;
interface CachedHeading {
  readonly traversalSeconds: number;
  readonly headingAt: HeadingAt;
}

// Heading follows travel at every lens distance; retain only the latest pace.
const CACHE = new WeakMap<RideProgressPlan, CachedHeading>();

/** Compiles continuous travel-aligned heading, independent of playback order. */
export function compileChaseHeading(
  route: RideProgressPlan,
  _distance: CameraDistanceId,
  traversalSeconds: number,
): HeadingAt {
  const cached = CACHE.get(route);
  if (cached?.traversalSeconds === traversalSeconds) return cached.headingAt;

  const divisions = Math.min(MAX_SAMPLES, Math.max(
    256,
    Math.ceil(route.totalDistanceKilometers / 0.005),
    Math.ceil(traversalSeconds * 60),
  ));
  const raw = Array.from({ length: divisions + 1 }, (_, index) =>
    smoothedRouteHeading(route, index / divisions, LOCAL_RADIUS_KILOMETERS, SMOOTHING_RADIUS_KILOMETERS));
  // Unwrap before averaging so southbound travel stays south across +/-180.
  const unwrapped = [raw[0]!];
  for (let index = 1; index <= divisions; index += 1) {
    unwrapped.push(unwrapped[index - 1]! + normalize(raw[index]! - raw[index - 1]!));
  }
  const sigma = SMOOTHING_SECONDS * divisions / traversalSeconds;
  const radius = Math.min(divisions, Math.ceil(sigma * 3));
  const bearings = unwrapped.map((bearing, index) => {
    let total = 0;
    let weights = 0;
    // Use actual neighboring samples, avoiding endpoint padding bias.
    for (let offset = -radius; offset <= radius; offset += 1) {
      if (index + offset < 0 || index + offset > divisions) continue;
      const weight = Math.exp(-0.5 * (offset / sigma) ** 2);
      total += (unwrapped[index + offset]! - bearing) * weight;
      weights += weight;
    }
    return bearing + total / weights;
  });

  const headingAt: HeadingAt = (journeyProgress) => {
    const position = clamp(journeyProgress, 0, 1) * divisions;
    const index = Math.min(divisions - 1, Math.floor(position));
    return normalize(bearings[index]!
      + (bearings[index + 1]! - bearings[index]!) * (position - index));
  };
  CACHE.set(route, { headingAt, traversalSeconds });
  return headingAt;
}

/** Shortest signed angle across the signed bearing boundary. */
function normalize(degrees: number): number {
  return ((degrees + 180) % 360 + 360) % 360 - 180;
}

/** Bounds one scalar value. */
function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
